import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaUsers,
  FaUserEdit,
  FaTrash,
  FaSearch,
  FaPlus,
  FaEye,
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaSpinner,
  FaExclamationTriangle,
  FaCheck,
  FaTimes,
  FaFilter
} from 'react-icons/fa';
import { database } from '../firebase';
import { ref as dbRef, onValue, remove, update } from 'firebase/database';

const AdminUser = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch users from Firebase
  useEffect(() => {
    const usersRef = dbRef(database, 'Dr/users');
    const unsubscribe = onValue(usersRef, (snapshot) => {
      if (snapshot.exists()) {
        const usersData = snapshot.val();
        const usersArray = Object.keys(usersData).map(key => ({
          id: key,
          ...usersData[key]
        }));
        setUsers(usersArray);
        setFilteredUsers(usersArray);
      } else {
        setUsers([]);
        setFilteredUsers([]);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error fetching users:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Filter users based on search and status
  useEffect(() => {
    let filtered = users;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.includes(searchTerm)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.status === statusFilter);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, statusFilter]);

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };


  const handleConfirmDelete = async () => {
    try {
      const userRef = dbRef(database, `Dr/users/${selectedUser.id}`);
      await remove(userRef);
      setShowDeleteModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      inactive: { bg: 'bg-red-100', text: 'text-red-800', label: 'Inactive' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-teal-50 to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="text-6xl text-teal-500 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600 text-lg">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-teal-50 to-emerald-50 p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
              <FaUsers className="text-teal-600" />
              User Management
            </h1>
            <p className="text-gray-600 mt-2">
              Manage and monitor all registered users
            </p>
          </div>
          <div className="flex items-center gap-3 text-gray-600">
            <FaCalendarAlt className="text-teal-600" />
            <span className="font-medium">{today}</span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 uppercase">Total Users</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{users.length}</p>
            </div>
            <FaUsers className="text-3xl text-blue-500 opacity-50" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 uppercase">Active</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">
                {users.filter(u => u.status === 'active').length}
              </p>
            </div>
            <FaCheck className="text-3xl text-green-500 opacity-50" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 uppercase">Inactive</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">
                {users.filter(u => u.status === 'inactive').length}
              </p>
            </div>
            <FaTimes className="text-3xl text-red-500 opacity-50" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 uppercase">Pending</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">
                {users.filter(u => u.status === 'pending').length}
              </p>
            </div>
            <FaExclamationTriangle className="text-3xl text-yellow-500 opacity-50" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <FaFilter className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-2xl font-bold text-gray-800">Users List</h2>
          <p className="text-gray-600 mt-1">
            Showing {filteredUsers.length} of {users.length} users
          </p>
        </div>
        
        <div className="overflow-x-auto">
          {filteredUsers.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <FaExclamationTriangle className="text-5xl mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No users found</p>
              <p className="text-sm mt-2">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your filters or search terms' 
                  : 'No users have registered yet'}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase">User</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase">Contact</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase">Address</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase">Joined</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-teal-50/30 transition-colors">
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center">
                          <FaUsers className="text-teal-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">ID: {user.id}</div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-5">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <FaEnvelope className="text-gray-400" />
                          <span className="text-gray-700">{user.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <FaPhone className="text-gray-400" />
                          <span className="text-gray-700">{user.phone}</span>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-5">
                      <div className="flex items-start gap-2">
                        <FaMapMarkerAlt className="text-gray-400 mt-1" />
                        <span className="text-sm text-gray-700 max-w-xs">
                          {user.address || 'Not provided'}
                        </span>
                      </div>
                    </td>
                    
                    <td className="px-6 py-5">
                      {getStatusBadge(user.status)}
                    </td>
                    
                    <td className="px-6 py-5">
                      <div className="text-sm text-gray-600">
                        {user.createdAt 
                          ? new Date(user.createdAt).toLocaleDateString('en-IN')
                          : 'Unknown'
                        }
                      </div>
                    </td>
                    
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewUser(user)}
                          className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                          title="View User"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                          title="Delete User"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* View User Modal */}
      {showViewModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-800">User Details</h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <FaTimes className="text-xl text-gray-600" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Personal Information */}
                <div className="md:col-span-2">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FaUsers className="mr-2 text-teal-600" /> Personal Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Full Name</label>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-800">{selectedUser.fullName || selectedUser.name || 'N/A'}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">User ID</label>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-800">{selectedUser.id || 'N/A'}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        {getStatusBadge(selectedUser.status)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Registration Date</label>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-800">
                          {selectedUser.createdAt 
                            ? new Date(selectedUser.createdAt).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })
                            : 'N/A'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="md:col-span-2">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FaEnvelope className="mr-2 text-teal-600" /> Contact Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Email Address</label>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-800">{selectedUser.email || 'N/A'}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Mobile Number</label>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-800">{selectedUser.mobile || selectedUser.phone || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className="md:col-span-2">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FaMapMarkerAlt className="mr-2 text-teal-600" /> Address Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-600 mb-1">Address</label>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-800">{selectedUser.address || 'N/A'}</span>
                      </div>
                    </div>
                    {selectedUser.city && (
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">City</label>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-800">{selectedUser.city}</span>
                        </div>
                      </div>
                    )}
                    {selectedUser.pincode && (
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Pincode</label>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-800">{selectedUser.pincode}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Account Information */}
                <div className="md:col-span-2">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FaUserEdit className="mr-2 text-teal-600" /> Account Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Role</label>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-800">{selectedUser.role || 'user'}</span>
                      </div>
                    </div>
                    {/* <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">UID</label>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-800 text-sm">{selectedUser.uid || 'N/A'}</span>
                      </div>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center">
              <FaTrash className="text-5xl text-red-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Delete User</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete <strong>{selectedUser.name}</strong>? This action cannot be undone.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={handleConfirmDelete}
                  className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  Delete
                </button>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUser;
